import * as React from 'react';
import cx from 'classnames';
import { addClassName } from '@elevio/styles/lib/utils/classes';
import { triggerCallback } from './clientApi';
import InnerHtml from './toolkit/InnerHtml';
import { Helmet } from './toolkit/meta';
import { BASE_CDN } from './utils/config';

const ARTICLE_CONTENT = 'js-article-content';
const SCROLL_ELEMENT_TIMER = 250;

type Props = {
  className?: string;
  articleId: string;
  html: string;
  noStyles?: boolean;
  onClickInterlink?: (articleId: string) => void;
  onClick?: (e: React.MouseEvent) => void;
};

const ArticleBody = ({
  className,
  articleId,
  html,
  noStyles,
  onClickInterlink,
  onClick,
}: Props) => {
  const onArticleClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    onClick && onClick(e);
  };

  // Add interlink event listeners
  React.useEffect(() => {
    function onclickArticle(event: MouseEvent, elem: HTMLElement) {
      const t = event.target as HTMLElement;
      const dataset = (elem && elem.dataset) || (t && t.dataset);

      if (
        dataset.elevioArticle ||
        dataset.elevioInline ||
        dataset.elevioModule
      ) {
        event.preventDefault();
        event.stopPropagation();
      }

      if (dataset.elevioArticle) {
        onClickInterlink && onClickInterlink(dataset.elevioArticle);
      }
    }

    const elems = document.querySelectorAll(
      `#${ARTICLE_CONTENT} a, #${ARTICLE_CONTENT} span[role='link'], #${ARTICLE_CONTENT} [data-elevio-article]`
    );

    // Just so we can clean them up
    const listeners: Array<(ev: MouseEvent) => void> = [];
    for (let i = 0; i < elems.length; ++i) {
      const a = elems[i] as HTMLElement;
      listeners[i] = (ev: MouseEvent) => onclickArticle(ev, a);
      a.addEventListener('click', listeners[i], false);
    }

    return () => {
      for (let i = 0; i < elems.length; ++i) {
        const a = elems[i] as HTMLElement;
        a.removeEventListener('click', listeners[i], false);
      }
    };
  }, [html, onClickInterlink]);

  // Trigger the on('article:loaded') event.
  React.useEffect(() => {
    triggerCallback('article:load', {
      articleId,
      body: html,
    });
  }, [html, articleId]);

  // Scroll to any anchored elements
  React.useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;

    const elemName = document.querySelector(`[name="${hash.slice(1)}"]`);
    const elemId = document.getElementById(hash.slice(1));

    if (elemName) {
      setTimeout(() => {
        elemName.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, SCROLL_ELEMENT_TIMER);
    } else if (elemId) {
      setTimeout(() => {
        elemId.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, SCROLL_ELEMENT_TIMER);
    }
  }, [html, articleId]);

  return (
    <>
      {!noStyles && (
        <Helmet>
          <link
            rel="stylesheet"
            type="text/css"
            href={`${BASE_CDN}/article-styles/article-min.css`}
          />
        </Helmet>
      )}
      <InnerHtml
        className={cx(
          addClassName('article-body'),
          'article',
          'articleBody',
          className
        )}
        html={html}
        onClick={onArticleClick}
        data-testid="article-body"
      />
    </>
  );
};

export default ArticleBody;
